import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Euro, Banknote, Moon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { getAuthHeaders } = useAuth();
  const { isDark, setIsDark } = useTheme();
  const [settings, setSettings] = useState(null);
  const [pourcentage, setPourcentage] = useState('');
  const [inclureEspeces, setIncluereEspeces] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`, getAuthHeaders());
      setSettings(response.data);
      setPourcentage(response.data.pourcentage_part.toString());
      setIncluereEspeces(response.data.inclure_especes);
      setIsDark(response.data.dark_mode);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/api/settings`,
        { 
          pourcentage_part: parseFloat(pourcentage),
          inclure_especes: inclureEspeces,
          dark_mode: isDark
        },
        getAuthHeaders()
      );
      toast.success('Paramètres enregistrés');
      fetchSettings();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Chargement...</div>;
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Paramètres</h1>
        <p className="text-muted-foreground mt-2">Configurez votre application</p>
      </div>

      <Card className="rounded-xl border-border/60 max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <div>
              <CardTitle>Ma part personnelle</CardTitle>
              <CardDescription>
                Définissez le pourcentage de votre part sur le chiffre d'affaires
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pourcentage">Pourcentage (%)</Label>
              <Input
                id="pourcentage"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={pourcentage}
                onChange={(e) => setPourcentage(e.target.value)}
                placeholder="Ex: 35"
                required
                data-testid="input-pourcentage"
              />
              <p className="text-sm text-muted-foreground">
                Actuellement : {settings?.pourcentage_part.toFixed(2)}%
              </p>
            </div>
            <Button type="submit" data-testid="btn-save-settings">
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-border/60 max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Banknote className="w-6 h-6 text-green-600" strokeWidth={1.5} />
            <div>
              <CardTitle>Déclaration des espèces</CardTitle>
              <CardDescription>
                Choisissez si vous souhaitez inclure les paiements en espèces dans vos déclarations
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border border-border/60 bg-muted/20">
              <div className="space-y-0.5">
                <Label htmlFor="inclure-especes" className="text-base font-medium cursor-pointer">
                  Inclure les espèces
                </Label>
                <p className="text-sm text-muted-foreground">
                  {inclureEspeces 
                    ? "Les paiements en espèces seront inclus dans les déclarations URSSAF et annuelles"
                    : "Les paiements en espèces seront exclus des déclarations"}
                </p>
              </div>
              <Switch
                id="inclure-especes"
                checked={inclureEspeces}
                onCheckedChange={setIncluereEspeces}
                data-testid="switch-inclure-especes"
              />
            </div>
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note importante :</strong> Cette option affecte uniquement les calculs dans les pages de déclarations. 
                Tous vos paiements restent enregistrés et visibles dans la page Paiements.
              </p>
            </div>
            <Button type="submit" data-testid="btn-save-settings">
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-border/60 max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Moon className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
            <div>
              <CardTitle>Mode sombre</CardTitle>
              <CardDescription>
                Activez le mode sombre pour une meilleure expérience nocturne
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border border-border/60 bg-muted/20">
              <div className="space-y-0.5">
                <Label htmlFor="dark-mode" className="text-base font-medium cursor-pointer">
                  Mode sombre
                </Label>
                <p className="text-sm text-muted-foreground">
                  {isDark 
                    ? "Interface en mode sombre activée"
                    : "Interface en mode clair"}
                </p>
              </div>
              <Switch
                id="dark-mode"
                checked={isDark}
                onCheckedChange={setIsDark}
                data-testid="switch-dark-mode"
              />
            </div>
            <Button type="submit" data-testid="btn-save-settings">
              Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="rounded-xl border-border/60 max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Euro className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
            <div>
              <CardTitle>Devise</CardTitle>
              <CardDescription>
                La devise utilisée pour tous les montants
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-lg font-medium">{settings?.devise} (€)</p>
        </CardContent>
      </Card>
    </div>
  );
}